import React, { useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';

import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import { useSamples } from '../services/api/KeynomeAPIHooks';
import { useCanUpload } from './utils/uploadHooks';

const SampleTypeSelect = ({ sampleType, setSampleType, isIllumina, canChangeType }) => {
  const { supportedSampleTypes: supportedSampleTypesData } = useOutletContext();
  const supportedSampleTypes = supportedSampleTypesData.map((sampleType) => sampleType.attributes.name);

  const filteredSampleTypes = isIllumina ?
    ['isolate'] :
    Array.from(['isolate', 'respiratory', 'metagenomic'].filter((sampleType) => supportedSampleTypes.includes(sampleType)));

  const handleChange = (event) => {
      setSampleType(event.target.value);
    };
  
  return (
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
      <FormControl fullWidth size="small" sx={{ padding: '8px 8px', m: 0, minWidth: 0 }}>
          <Select
            value={sampleType}
            onChange={handleChange}
          sx={{ height: '32px', fontSize: '13px' }}
            disabled={!canChangeType}
          >
            {filteredSampleTypes.map((sampleType) => (
            <MenuItem key={sampleType} value={sampleType} sx={{ fontSize: '13px', py: 0.5 }}>{sampleType}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
    );
}

function AddSamplesFileListItem(props) {
    const {
        activeRun,
        isIllumina,
        stagedFile,
        removeStagedFile,
        setStagedFileAttribute,
    } = props;
    const { sampleName, R1: file, R2: r2File, sampleType, status: uploadingState } = stagedFile;

    const { data: samples } = useSamples(activeRun?.id);
    const { canUpload, isLoading } = useCanUpload(samples, sampleName);

    useEffect(() => {
      setStagedFileAttribute(activeRun.id, sampleName, "canUpload", canUpload);
    }, [activeRun, sampleName, canUpload, setStagedFileAttribute])

    const totalFileSize = isIllumina ? ((file?.size | 0) + (r2File?.size | 0)) : file.size
    const i = totalFileSize === 0 ? 0 : Math.floor(Math.log(totalFileSize) / Math.log(1024));
    const humanReadableFileSize = ((totalFileSize / Math.pow(1024, i)).toFixed(2)) * 1 + ' ' + ['B', 'kB', 'MB', 'GB', 'TB'][i];

    let icons = null;
    if (!uploadingState) {
        icons = (
            <>
            {((!isIllumina || (file !== null && r2File !== null))) ? (
              (!canUpload && !isLoading) ? (<small style={{marginTop: "10px", marginRight: "10px", color: "grey"}}><b>(Already exists - rename to upload)</b></small>) : null ) : null}
            <Button
              size="small"
              sx={{
                marginLeft: "5px",
                marginRight: "10px",
                padding: '4px 10px',
                lineHeight: 1.2,
                display: 'inline-flex',
                alignItems: 'center'
              }}
              variant="outlined"
              color="error"
              aria-label="cancel"
              onClick={() => removeStagedFile(activeRun.id, sampleName)}
              endIcon={<DeleteIcon sx={{ fontSize: 18 }} />}
            >
              CANCEL
            </Button>
            </>
        )
    } else if (uploadingState === 'success') {
        icons = (
            <CheckCircleOutlineIcon fontSize="large" color="success" sx={{marginTop: "7px", marginRight: "30px"}} />
        )
    } else if (uploadingState === 'failed') {
        icons = (
            <ErrorOutlineIcon fontSize="large" color="warning" sx={{marginTop: "7px", marginRight: "30px"}} />
        )
    } else {
        const steps = {
            creating_lib_seq: 1,
            creating_concatenated_fastqgz: 2,
            uploading_file: 3
        }

        let progress = (steps[uploadingState] / 5) * 100
        icons = (
                <Box sx={{paddingTop: "10px"}}>
              <CircularProgress
                    sx={{marginRight: "35px"}}
                    size={27}
                    value={uploadingState === 'uploading_file' ? "" : progress}
                    variant={uploadingState === 'uploading_file'? "indeterminate" : "determinate"}
                  />
            </Box>
        )
    }

    return (
      <Box>
        <Grid
          container
          sx={{
              pl: 2, pr: 1, textAlign: 'center', borderBottom: '#bdbdbd 2px solid', fontSize: 15
          }}
        >
          <Grid item xs={12} sm={2} sx={{ textAlign: 'left', display: 'flex', alignItems: 'center' }}>
            <p style={{ overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis', margin: 0 }}>
              <b>{sampleName}</b>
            </p>
          </Grid>
          <Grid item xs={1.5} sx={{ textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            {canUpload ?
              <SampleTypeSelect
                sampleType={sampleType}
                setSampleType={(type) => setStagedFileAttribute(activeRun.id, sampleName, 'sampleType', type)}
                isIllumina={isIllumina}
                canChangeType={uploadingState === null}
              />
              : null
            }
          </Grid>
          <Grid item xs={12} sm={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', px: 1 }}>
            <p style={{ margin: 0, textAlign: 'center', wordBreak: 'break-all', overflowWrap: 'anywhere', padding: '0 6px' }}>
              {(file && file.path) ? file.path.replace('./', '') : '-'}
            </p>
          </Grid>
        {
            isIllumina ? 
            (
              <Grid item xs={12} sm={2} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', px: 1 }}>
                <p style={{ margin: 0, textAlign: 'center', wordBreak: 'break-all', overflowWrap: 'anywhere', padding: '0 6px' }}>
                  {(r2File && r2File.path) ? r2File.path.replace('./', '') : '-'}
                </p>
              </Grid>
            ) : null

        }
          <Grid item xs={12} sm={1.5} sx={{ textAlign: 'center' }}>
            <p>{ humanReadableFileSize }</p>
          </Grid>
        {
            isIllumina ? null : (
                  <Grid item xs={12} sm={2} sx={{ textAlign: 'center' }}>
                    <p>{file.lastModifiedDate ? file.lastModifiedDate.toLocaleString() : '-'}</p>
                  </Grid>
            )
        }
          <Grid item xs={12} sm={3} sx={{ textAlign: 'right', display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            {icons}
          </Grid>
        </Grid>
      </Box>
    )
}

export default AddSamplesFileListItem;
